/**
 * typeSeoContent.js
 *
 * In-code SEO content map for NATIONAL trailer-TYPE hub pages
 * (e.g. /dump-trailer-rental, /motorcycle-trailer-rental, /car-hauler-rental).
 *
 * WHY IN-CODE (mirrors stateSeoContent.js):
 *   - No Strapi write token is available for programmatic seoCategory records
 *   - Version-controlled = PR-reviewable = no CMS accidents
 *   - Each hub is a fixed, hand-authored, high-value page (only a handful of
 *     types), so a curated in-code map beats a templated CMS record here.
 *
 * Each TYPE entry drives the whole page:
 *   trailerType   – the exact publicData.trailerType value used to filter
 *                   listings nationally (no geo bounds). GROUND TRUTH from live
 *                   listing data (see counts noted per type).
 *   slug          – URL segment: /<slug>  (exact-match rental keyword)
 *   h1            – hero H1
 *   heroSub       – hero subheading
 *   heroImage     – public asset path for the hero banner
 *   seoTitle      – <title>
 *   seoDescription– <meta name="description">
 *   quickAnswer   – featured-snippet-bait paragraph (real price range injected
 *                   at render when live listings exist; never fabricated)
 *   intro         – editorial body: array of { heading, body } sections
 *   subSections   – the 2-col "what size / what tows it / cost / license" cards
 *   faqs          – array of { question, answer } (targets real question queries)
 *   relatedBlogs  – interlink targets (existing guide slugs, verified to exist)
 *   priceHint     – fallback price phrasing when zero live listings (rare)
 *
 * ADD A NEW TYPE: copy a block, change trailerType/slug/content. Register its
 * slug in routeConfiguration.js (see SeoTypePage routes) and in the sitemap.
 */

// The full set of type hubs (ordered by launch priority / demand x inventory).
export const TYPE_HUBS = [
  // ── MOTORCYCLE ── demand ~2,894 impr/90d · inventory 360 listings ──────────
  {
    trailerType: 'motorcycle-trailer',
    slug: 'motorcycle-trailer-rental',
    h1: 'Motorcycle Trailer Rental',
    // Visible hero heading: action-oriented + "near you" intent (the clean h1
    // above is used for the <title>/schema/breadcrumb; the hero can be punchier).
    heroHeading: 'Find a Motorcycle Trailer Rental Near You',
    noun: 'Motorcycle Trailer', // clean subject noun for derived headings/CTAs
    nounPlural: 'Motorcycle Trailers',
    heroSub:
      'Rent a motorcycle trailer from local owners for safe, secure bike transport. Open or enclosed, single or multi-bike. Message the owner free before you book.',
    heroImage: '/static/seo/hub-motorcycle-trailer.jpg',
    seoTitle: 'Motorcycle Trailer Rental | Rent From Local Owners | BigLoads',
    seoDescription:
      'Rent a motorcycle trailer near you from local owners on BigLoads. Open & enclosed options for single or multiple bikes. No membership fees, free to message owners.',
    quickAnswer: pr =>
      `Motorcycle trailer rentals on BigLoads ${
        pr ? `run ${pr}` : 'typically run about $40 to $110 per day'
      } depending on whether it's open or enclosed and how many bikes it carries. You rent directly from local owners near you, message them free before booking, and pick up locally, with no national-chain markup or membership.`,
    intro: [
      {
        heading: 'Renting a Motorcycle Trailer: What You Need to Know',
        body:
          "A motorcycle trailer is the safest way to move a bike you don't want to (or can't) ride, whether it's a long-distance move, a track day, a new purchase, or a bike that's down. Instead of paying rental-chain rates and deposits, BigLoads connects you directly with local owners who set fair daily prices and let you message them free before you commit. You get the right trailer for your bike, close to home.",
      },
    ],
    subSections: [
      {
        heading: 'Open or enclosed?',
        body:
          'Open motorcycle trailers are cheaper and easier to load, and fine for short local hauls in good weather. Enclosed trailers protect your bike from weather, road debris, and prying eyes, ideal for long distances or a valuable bike.',
      },
      {
        heading: 'One bike or several?',
        body:
          'Single-rail and single-bike trailers keep it light and easy to tow. If you\'re moving two or more bikes (or riding with friends), look for multi-rail or multi-bike trailers, many owners list exactly that.',
      },
      {
        heading: 'What can I tow it with?',
        body:
          'Most open motorcycle trailers are light enough for almost any SUV, crossover, or truck. Enclosed multi-bike trailers are heavier, so check the owner\'s stated weight against your vehicle\'s tow rating.',
      },
      {
        heading: 'How much does it cost?',
        body:
          'On BigLoads, motorcycle trailer rentals typically run $40 to $110 per day. Owners set their own rates, there are no BigLoads membership fees, and messaging is always free.',
      },
    ],
    faqs: [
      {
        question: 'How much does it cost to rent a motorcycle trailer?',
        answer:
          'On BigLoads, motorcycle trailers typically rent for about $40 to $110 per day depending on whether it\'s open or enclosed and how many bikes it holds. Owners set their own rates and there are no membership fees.',
      },
      {
        question: 'What size trailer do I need to haul a motorcycle?',
        answer:
          'A single-bike open trailer around 8 to 10 ft handles most street bikes and cruisers. For touring bikes, baggers, or multiple motorcycles, look for a larger open or enclosed trailer with multiple rails or chocks.',
      },
      {
        question: 'Can I rent a trailer for two motorcycles?',
        answer:
          'Yes. Many owners on BigLoads list multi-bike motorcycle trailers with two or more wheel chocks or rails. Filter or browse the listings above, and message the owner to confirm it fits both bikes.',
      },
      {
        question: 'Should I rent an open or enclosed motorcycle trailer?',
        answer:
          'Open trailers are cheaper and simpler for short local hauls. Enclosed trailers cost a bit more but protect your bike from weather, debris, and theft, which is worth it for long distances or a valuable motorcycle.',
      },
      {
        question: 'Are there motorcycle trailers for rent near me?',
        answer:
          'BigLoads lists motorcycle trailers from local owners in most major markets. Browse by state and city above, or search your location to see what\'s available near you.',
      },
    ],
    relatedBlogs: [
      { slug: 'motorcycle-trailer-rental-guide', label: 'Motorcycle Trailer Rental Guide: Costs, Types & How to Load' },
      { slug: 'open-vs-enclosed-motorcycle-trailer', label: 'Open vs Enclosed Motorcycle Trailer: Which to Rent' },
      { slug: 'motorcycle-trailer-size', label: 'What Size Trailer Do You Need to Haul a Motorcycle?' },
      { slug: 'how-to-tie-down-a-motorcycle-on-a-trailer', label: 'How to Tie Down a Motorcycle So It Never Shifts' },
    ],
    priceHint: '$40 to $110 per day',
  },

  // ── DUMP ── demand ~1,864 impr/90d · inventory 302 listings ────────────────
  {
    trailerType: 'dump-trailer',
    slug: 'dump-trailer-rental',
    h1: 'Dump Trailer Rental',
    heroHeading: 'Find a Dump Trailer Rental Near You',
    noun: 'Dump Trailer',
    nounPlural: 'Dump Trailers',
    heroSub:
      'Rent a dump trailer for dirt, gravel, debris, demolition, and junk removal, directly from local owners. No dealership markups, no membership fees. Message the owner free.',
    heroImage: '/static/seo/hub-dump-trailer.jpg',
    seoTitle: 'Dump Trailer Rental | Rent From Local Owners | BigLoads',
    seoDescription:
      'Rent a dump trailer near you from local owners on BigLoads. 10 to 16 ft, 6,000 to 14,000 lb for dirt, gravel, demo & junk removal. No membership fees, free to message owners.',
    quickAnswer: pr =>
      `Dump trailer rentals on BigLoads ${
        pr ? `run ${pr}` : 'typically run about $65 to $140 per day'
      } depending on size (10 to 16 ft) and capacity (6,000 to 14,000 lb). You rent directly from local owners near you, message them free before booking, and pick up locally, with no national-chain markup or membership.`,
    intro: [
      {
        heading: 'Renting a Dump Trailer: What You Need to Know',
        body:
          'A dump trailer is the fastest way to move and unload heavy loose material, soil, gravel, mulch, concrete, demolition debris, and household junk, without paying for a dumpster or a hauling service. Instead of renting from a national chain at premium daily rates plus deposits and fees, BigLoads connects you directly with local owners who set fair daily rates and let you message them free before you commit.',
      },
    ],
    subSections: [
      {
        heading: 'What size dump trailer do I need?',
        body:
          '10 to 12 ft trailers (6,000 to 9,000 lb) handle most yard cleanups, single-room demos, and small landscaping jobs. 14 to 16 ft trailers (10,000 to 14,000 lb) suit contractors moving gravel, concrete, or full-house debris.',
      },
      {
        heading: 'What can I tow it with?',
        body:
          'Most 10 to 12 ft dump trailers can be towed by a half-ton pickup (F-150, Silverado 1500, Ram 1500). Heavier 14 to 16 ft loads want a 3/4-ton or larger. Check the owner\'s stated GVWR against your truck.',
      },
      {
        heading: 'How much does it cost?',
        body:
          'On BigLoads, dump trailer rentals typically run $65 to $140 per day. You\'ll pay the owner\'s daily rate; there are no BigLoads membership fees and messaging is always free.',
      },
      {
        heading: 'Do I need a special license?',
        body:
          'For most consumer dump trailers under 10,000 lb GVWR, a standard driver\'s license is fine in most states. Always confirm your state\'s towing rules for heavier trailers.',
      },
    ],
    faqs: [
      {
        question: 'How much does it cost to rent a dump trailer for a day?',
        answer:
          'On BigLoads, dump trailers typically rent for $65 to $140 per day depending on size and capacity. Owners set their own rates and there are no membership fees.',
      },
      {
        question: 'How big of a dump trailer can I tow with a half-ton truck?',
        answer:
          'A half-ton pickup can generally tow a 10 to 12 ft dump trailer up to about 9,000 lb GVWR. For 14 to 16 ft trailers or heavier loads, use a 3/4-ton or larger truck.',
      },
      {
        question: 'Do I need a deposit to rent a dump trailer?',
        answer:
          'Deposit policies are set by each owner. Message the owner directly (free) to confirm their deposit, insurance, and pickup terms before booking.',
      },
      {
        question: 'What can I haul in a rented dump trailer?',
        answer:
          'Dirt, gravel, mulch, concrete, demolition debris, roofing, yard waste, and household junk are all common. Confirm any weight or material limits with the owner.',
      },
      {
        question: 'Are there dump trailers for rent near me?',
        answer:
          'BigLoads lists dump trailers from local owners in most major markets. Browse by state and city above, or search your location to see what\'s available near you.',
      },
    ],
    relatedBlogs: [
      { slug: 'complete-guide-to-renting-a-dump-trailer', label: 'The Complete Guide to Renting a Dump Trailer' },
      { slug: 'dump-trailer-rental', label: 'Dump Trailer Rental: Costs, Sizes & What You Need to Know' },
      { slug: 'how-to-load-a-dump-trailer', label: 'How to Load a Dump Trailer: Weight, Balance & Safety' },
      { slug: 'dump-trailer-vs-dump-truck-rental', label: 'Dump Trailer vs Dump Truck Rental: Which Is Cheaper' },
    ],
    priceHint: '$65 to $140 per day',
  },

  // ── CAR HAULER ── demand ~153 impr/90d + "car trailer" intent · inventory 612 (most) ──
  {
    trailerType: 'car-hauler',
    slug: 'car-hauler-rental',
    h1: 'Car Hauler Trailer Rental',
    heroHeading: 'Find a Car Hauler Rental Near You',
    noun: 'Car Hauler',
    nounPlural: 'Car Haulers',
    heroSub:
      'Rent a car hauler or car trailer from local owners to transport a car, truck, or SUV. Open and enclosed options. No membership fees, message the owner free.',
    heroImage: '/static/seo/hub-car-hauler.jpg',
    seoTitle: 'Car Hauler Trailer Rental | Rent a Car Trailer | BigLoads',
    seoDescription:
      'Rent a car hauler or car trailer near you from local owners on BigLoads. Open & enclosed options for cars, trucks & SUVs. No membership fees, free to message owners.',
    quickAnswer: pr =>
      `Car hauler and car trailer rentals on BigLoads ${
        pr ? `run ${pr}` : 'typically run about $60 to $150 per day'
      } depending on whether it's an open or enclosed trailer and its length. You rent directly from local owners near you, message them free before booking, and pick up locally, with no national-chain markup or membership.`,
    intro: [
      {
        heading: 'Renting a Car Hauler: What You Need to Know',
        body:
          "A car hauler (also called a car trailer) is what you want for moving a vehicle you can't or don't want to drive, a project car, a new purchase, a track car, or a non-runner. Instead of paying rental-chain rates and deposits, BigLoads connects you directly with local owners who set fair daily prices and let you message them free before you commit.",
      },
    ],
    subSections: [
      {
        heading: 'Open or enclosed car trailer?',
        body:
          'Open car haulers are the most common and most affordable, and fine for most moves. Enclosed car trailers protect the vehicle from weather and road debris, worth it for classics, exotics, or long-distance transport.',
      },
      {
        heading: 'What length do I need?',
        body:
          'Most cars fit an 18 to 20 ft open car hauler. Larger trucks, SUVs, and dually pickups may need a 20 to 24 ft trailer. Check the owner\'s deck length and rating against your vehicle.',
      },
      {
        heading: 'What can I tow it with?',
        body:
          'A loaded car hauler is heavy. Most need a 3/4-ton or larger truck, and some full-size cars can be moved with a capable half-ton. Always check the owner\'s stated GVWR against your truck\'s tow rating.',
      },
      {
        heading: 'How much does it cost?',
        body:
          'On BigLoads, car hauler rentals typically run $60 to $150 per day. Owners set their own rates, there are no BigLoads membership fees, and messaging is always free.',
      },
    ],
    faqs: [
      {
        question: 'How much does it cost to rent a car hauler?',
        answer:
          'On BigLoads, car haulers and car trailers typically rent for about $60 to $150 per day depending on whether it\'s open or enclosed and its length. Owners set their own rates and there are no membership fees.',
      },
      {
        question: 'What size car trailer do I need?',
        answer:
          'Most cars fit an 18 to 20 ft open car hauler. Larger trucks, SUVs, and dually pickups may need a 20 to 24 ft trailer. Check the owner\'s deck length and weight rating against your vehicle.',
      },
      {
        question: 'What truck do I need to tow a loaded car hauler?',
        answer:
          'A loaded car hauler is heavy, so most require a 3/4-ton or larger truck. Some lighter cars can be moved with a capable half-ton. Always match the owner\'s stated GVWR to your truck\'s tow rating.',
      },
      {
        question: 'Can I rent an enclosed car trailer?',
        answer:
          'Yes. Many owners on BigLoads list enclosed car trailers, which protect the vehicle from weather and debris, ideal for classics, exotics, or long hauls. Browse the listings above and message the owner.',
      },
      {
        question: 'Are there car haulers for rent near me?',
        answer:
          'BigLoads lists car haulers and car trailers from local owners in most major markets. Browse by state and city above, or search your location to see what\'s available near you.',
      },
    ],
    relatedBlogs: [
      { slug: 'car-hauler-trailer-rentals', label: 'Car Hauler Trailer Rentals: Find the Right Trailer' },
      { slug: 'how-much-does-it-cost-to-rent-a-car-hauler', label: 'How Much Does It Cost to Rent a Car Hauler?' },
    ],
    priceHint: '$60 to $150 per day',
  },
];

// Fast lookup by slug (used by the container + loadData).
export const getTypeHubBySlug = slug =>
  TYPE_HUBS.find(h => h.slug === (slug || '').toLowerCase()) || null;

// All slugs (used to register routes + sitemap).
export const TYPE_HUB_SLUGS = TYPE_HUBS.map(h => h.slug);
